import * as React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants/constants';

import Demo, {
  DemoToggleButtonGroupControl,
} from '@/components/Demo';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';

import Ball, { Variant } from './FadeOutBall';
import CheckmarkButton from './CheckmarkButton';
import { constructSnippet } from './WithAndWithoutFromDemo.helpers';

interface Props {
  initialVariant?: Variant;
  initialIsToggled?: boolean;
  codeOutput: React.ReactNode;
}

function WithAndWithoutFromDemo({
  codeOutput,
  initialIsToggled = false,
}: Props) {
  const [isToggled, setIsToggled] = React.useState(initialIsToggled);

  return (
    <OuterWrapper>
      <Demo
        initialValues={{
          variant: 'with-from',
        }}
        outputWrapper={null}
        controlPosition="top"
        customGrid={{
          gridTemplateColumns: '1fr',
        }}
        handleStateChange={() => {
          setIsToggled(false);
        }}
        controls={[
          <DemoToggleButtonGroupControl
            key="variant"
            id="variant"
            label="Variant"
            inline
            options={[
              { label: 'Full', value: 'with-from' },
              { label: 'Partial', value: 'without-from' },
            ]}
          />,
        ]}
      >
        {({ variant }) => {
          const codeSnippet = constructSnippet(variant);

          return (
            <Wrapper>
              <Demonstration data-can-be-annotated="false">
                <Balls>
                  {[1, 0.6, 0.3].map((opacity, index) => (
                    <Ball
                      key={index}
                      targetOpacity={opacity}
                      isToggled={isToggled}
                      variant={variant}
                    />
                  ))}
                </Balls>
                <Button
                  isToggled={isToggled}
                  onClick={() => setIsToggled(!isToggled)}
                >
                  Apply keyframe
                </Button>
              </Demonstration>
              <SnippetWrapper>
                <CodeSnippetProvider lang="css" code={codeSnippet}>
                  {codeOutput}
                </CodeSnippetProvider>
              </SnippetWrapper>
            </Wrapper>
          );
        }}
      </Demo>
    </OuterWrapper>
  );
}

// On larger windows, we want the text in the code snippet to line up with the body paragraphs:
const OuterWrapper = styled.div`
  @media (min-width: 50rem) {
    margin-inline: -16px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  flex-wrap: wrap;
  gap: 16px;

  @media ${BREAKPOINTS.smAndSmaller} {
    flex-direction: column;
  }

  @media ${BREAKPOINTS.smAndLarger} {
    margin: -16px;
    margin-top: 0;
  }
`;

const SnippetWrapper = styled.div`
  --code-font-size: 0.875rem;
  --code-line-height: 1.325rem;
  flex: 1;
  min-width: 20.5rem;
  min-height: 14.325rem;
  border: 1px solid var(--color-gray-300);
  border-radius: 8px;
  text-align: left;
  white-space: pre-wrap;

  @media ${BREAKPOINTS.smAndSmaller} {
    width: 100%;
    min-width: 0;
    min-height: 0;
    max-width: 100%;
    overflow-x: auto;
  }
`;

const Demonstration = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 32px;
  min-width: 20rem;
  padding: 16px;
  border: 1px solid var(--color-gray-300);
  border-radius: 8px;

  @media ${BREAKPOINTS.smAndSmaller} {
    padding-block: 32px;
    min-width: 0;
    max-width: 100%;
    overflow-x: auto;
  }
`;

const Balls = styled.div`
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  gap: 16px;
`;

const Button = styled(CheckmarkButton)`
  width: 11rem;
  height: 3rem;
  padding: 0;
`;

export default WithAndWithoutFromDemo;
